import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Bell, AlertTriangle, MessageSquare, FileText, Clock, CheckCircle2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const SEVERITY_ICON = {
  critical: AlertTriangle,
  warning: Clock,
  info: Bell,
  success: CheckCircle2,
};

const SEVERITY_COLOR = {
  critical: 'text-red-600 bg-red-100',
  warning: 'text-orange-600 bg-orange-100',
  info: 'text-blue-600 bg-blue-100',
  success: 'text-emerald-600 bg-emerald-100',
};

export const NotificationsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('todos');

  const fetchAll = async () => {
    try {
      const res = await api.get('/notifications/all');
      setItems(res.data.notifications || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const filtered = tab === 'todos' ? items :
    tab === 'criticas' ? items.filter(i => i.severity === 'critical') :
    tab === 'prazos' ? items.filter(i => i.type === 'deadline') :
    items;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto" data-testid="notifications-page">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-[#1d4ed8]" />
              Central de Notificações
            </h1>
            <p className="text-slate-500">Alertas, prazos e mensagens em tempo real</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-notifications-btn">
            Atualizar
          </Button>
        </div>

        {/* Filtros */}
        <div className="flex gap-2 mb-6 border-b border-slate-200">
          {[
            { id: 'todos', label: 'Todas', count: items.length },
            { id: 'criticas', label: 'Críticas', count: items.filter(i => i.severity === 'critical').length },
            { id: 'prazos', label: 'Prazos', count: items.filter(i => i.type === 'deadline').length },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`filter-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-[#1d4ed8] text-[#1d4ed8]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label} <span className="ml-1 text-xs text-slate-400">({t.count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">Nada novo por aqui</p>
              <p className="text-sm mt-1">Você está em dia com tudo!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((n, idx) => {
              const Icon = SEVERITY_ICON[n.severity] || Bell;
              return (
                <Card
                  key={n.id || idx}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${!n.read ? 'border-l-4 border-l-[#1d4ed8]' : ''}`}
                  onClick={() => n.link && navigate(n.link)}
                  data-testid={`notification-item-${idx}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.info}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{n.title}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{n.message}</p>
                      <p className="text-xs text-slate-400 mt-1.5">
                        {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: ptBR }) : ''}
                      </p>
                    </div>
                    {!n.read && <Badge variant="secondary" className="text-[10px]">Nova</Badge>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default NotificationsPage;
